import { ClientDataService, IClientDataDoc } from '../../../services/clientData.service'
import { IRootState } from '../../../store'
import { IRules } from '../../pcr/models/rules.V1'
import { RulesConstants } from '../store/rules.constants'
import { IRuleFunction } from '../store/rules.types'
import RulesUtils from '../util/rules.util.master'
import validationService from '../services/validation.services'
import Logger from '../../../services/logger.service'

export class RulesService extends ClientDataService {
  private static _INTERNAL_SERVICE_ID_TO_LIST_OF_RULE_FUNCTION_MAP: Map<string, Array<IRuleFunction>> = new Map()

  public NATIONAL_RULES = 'validation_nemsis_national'

  async getRules(): Promise<IClientDataDoc | undefined | null> {
    return await this.getDocument(RulesConstants.RULES_DOC)
  }

  async getNationalRules(): Promise<IClientDataDoc | undefined | null> {
    return await this.getDocument(this.NATIONAL_RULES)
  }

  static loadRulesForService(serviceId: string, rules: IRules[]): number {
    const ruleFunctions = new Array<IRuleFunction>()

    rules.forEach((rule: IRules) => {
      if (rule.active && rule.script) {
        try {
          const ruleFunction: IRuleFunction = {
            id: rule.id,
            name: rule.name,
            active: rule.active,
            priority: rule.priority,
            description: rule.description,
            sourceType: rule.sourceType,
            errorMessage: rule.errorMessage,
            namespaces: rule.namespaces,
            dataPoints: rule.dataPoints,
            displayKey: rule.displayKey,
            setActions: rule.setActions,
            displayAction: rule.displayAction,
            theFunction: new Function(RulesUtils.name, RulesConstants.RULES_RETURN + rule.script),
          }

          ruleFunctions.push(ruleFunction)
        } catch (e) {
          Logger.error(`Unable to load rule with id: ${rule.id} for service: ${serviceId} - Reason: ${e}`)
        }
      }
    })

    RulesService._INTERNAL_SERVICE_ID_TO_LIST_OF_RULE_FUNCTION_MAP.set(serviceId, ruleFunctions)

    return ruleFunctions.length
  }

  static runRulesForService(serviceId: string, data: IRootState, onlyRunRulesInNamespaces = false): number {
    const ruleFunctions = RulesService._INTERNAL_SERVICE_ID_TO_LIST_OF_RULE_FUNCTION_MAP.get(serviceId)

    if (!ruleFunctions) {
      return 0
    }

    let numberOfRulesRan = 0

    ruleFunctions.forEach((rule: IRuleFunction) => {
      const runRule = RulesService.canRuleRun(rule, data, onlyRunRulesInNamespaces)

      const errorLabel = validationService.getInputValidationElementForId(rule.displayAction)

      if (!errorLabel) {
        return
      }

      if (runRule) {
        ++numberOfRulesRan

        const innerFunc = rule.theFunction()

        const result = innerFunc(RulesUtils)(data)

        errorLabel.innerHTML = !result ? rule.errorMessage : ''
      } else {
        if (errorLabel.innerHTML === rule.errorMessage) {
          errorLabel.innerHTML = ''
        }
      }
    })

    return numberOfRulesRan
  }

  private static canRuleRun(rule: IRuleFunction, data: IRootState, onlyRunRulesInNamespaces: boolean): boolean {
    if (!rule.active || !rule.theFunction) {
      return false
    }

    const validationState = data.validation

    if (onlyRunRulesInNamespaces && rule.namespaces && validationState.currentNamespace) {
      if (rule.namespaces.indexOf(validationState.currentNamespace) === -1) {
        return false
      }
    }

    if (rule.setActions) {
      for (const setAction of rule.setActions) {
        const isRuleReadyForAction = validationState.ruleReadyActions.has(setAction)

        if (!isRuleReadyForAction) {
          return false
        }
      }
    }

    return true
  }
}
